import React, { createContext, useContext, useState, useEffect } from 'react'
import {
  Cpu,
  TrendingUp,
  AlertTriangle,
  ArrowLeftRight,
  CheckCircle2,
  RefreshCw,
  Bell,
  Info,
  GitCompareArrows,
  Zap,
  LucideIcon,
} from 'lucide-react'
import {
  NotificationType,
  NotificationCondition,
  NotificationDetails,
} from './useNotifications'

// --- Types ---

export type AlertCategory =
  | 'Forecast update'
  | 'Market signal'
  | 'Model revision'

export interface AlertPreset {
  id: string
  name: string
  description: string
  category: AlertCategory
  notification_type: NotificationType
  icon: LucideIcon
  iconColor: string
  iconBg: string
  glassIconColor: string
  glassIconBg: string
  default_details: Partial<NotificationDetails>
  required_inputs: string[]
}

export interface AlertRule {
  id: string
  alert_name: string
  notification_type: NotificationType
  workspace_id?: string
  workspace_name?: string
  rule_details: {
    mode?: 'any' | 'all'
    min_forecast_minutes?: number
    model_scope?: string[]
    conditions?: NotificationCondition[]
    locations?: string[]
    models?: string[]
    change_type?: 'absolute' | 'percent' | 'both'
    abs_threshold?: number
    pct_threshold?: number
  }
  delivery: { in_app: boolean; push: boolean }
  preset_id: string
  created_at: string
  updated_at: string
}

// --- Power market zone & model constants ---

export const POWER_MARKET_ZONES: { label: string; zones: string[] }[] = [
  {
    label: 'Central Western Europe',
    zones: ['DE-LU', 'FR', 'NL', 'BE', 'AT', 'CH'],
  },
  {
    label: 'Nordics',
    zones: [
      'DK1',
      'DK2',
      'SE1',
      'SE2',
      'SE3',
      'SE4',
      'NO1',
      'NO2',
      'NO3',
      'NO4',
      'NO5',
      'FI',
    ],
  },
  {
    label: 'Iberia & Italy',
    zones: [
      'ES',
      'PT',
      'IT-North',
      'IT-Centre-North',
      'IT-Centre-South',
      'IT-South',
      'IT-Sicily',
      'IT-Sardinia',
    ],
  },
  {
    label: 'Central Eastern Europe',
    zones: ['PL', 'CZ', 'HU', 'SK', 'RO', 'BG', 'HR', 'SI'],
  },
  {
    label: 'UK & Ireland',
    zones: ['GB', 'IE', 'IE-SEM'],
  },
]

export const POWER_MODELS = [
  'EPT-2',
  'EPT-2e',
  'EC-IFS',
  'EC AIFS',
  'GFS',
  'ICON',
]

export const POWER_VARIABLES: {
  name: string
  unit: string
  operator: string
  operator_phrase: string
  values: number[]
}[] = [
  {
    name: 'Wind Generation',
    unit: 'GW',
    operator: '>',
    operator_phrase: 'above',
    values: [1, 2, 5, 10, 15, 20, 25, 30, 40, 50],
  },
  {
    name: 'Solar Generation',
    unit: 'GW',
    operator: '>',
    operator_phrase: 'above',
    values: [1, 2, 5, 10, 15, 20, 25, 30],
  },
  {
    name: 'Total Load',
    unit: 'GW',
    operator: '>',
    operator_phrase: 'above',
    values: [20, 30, 40, 50, 60, 70, 80, 90],
  },
  {
    name: 'Residual Load',
    unit: 'GW',
    operator: '<',
    operator_phrase: 'below',
    values: [-5, 0, 5, 10, 15, 20, 25, 30],
  },
]

// --- Presets ---

export const PRESETS: AlertPreset[] = [
  // Forecast Update (data_availability)
  {
    id: 'forecast-update',
    name: 'Forecast Update',
    description:
      'Notify when new forecast runs become available. Choose whether to trigger on any single model or wait for full coverage.',
    category: 'Forecast update',
    notification_type: 'data_availability',
    icon: CheckCircle2,
    iconColor: 'text-blue-400',
    iconBg: 'bg-blue-500/10',
    glassIconColor: 'text-white/70',
    glassIconBg: 'bg-white/[0.06]',
    default_details: {
      mode: 'any',
      min_forecast_minutes: 1440,
    },
    required_inputs: ['Model scope', 'Trigger frequency', 'Expiration'],
  },

  // Market Signal — Weather (threshold)
  {
    id: 'market-weather',
    name: 'Weather Alert',
    description:
      'Notify when a weather variable crosses a threshold you define.',
    category: 'Market signal',
    notification_type: 'threshold',
    icon: TrendingUp,
    iconColor: 'text-emerald-400',
    iconBg: 'bg-emerald-500/10',
    glassIconColor: 'text-white/70',
    glassIconBg: 'bg-white/[0.06]',
    default_details: {
      conditions: [
        {
          variable_display_name: 'Wind Speed',
          operator: '>',
          operator_phrase: 'above',
          threshold: 15,
          unit: 'm/s',
        },
      ],
    },
    required_inputs: ['Variable', 'Threshold value', 'Location scope'],
  },
  {
    id: 'market-temp-drop',
    name: 'Temperature Drop',
    description: 'Notify when temperature falls below a specific value.',
    category: 'Market signal',
    notification_type: 'threshold',
    icon: TrendingUp,
    iconColor: 'text-emerald-400',
    iconBg: 'bg-emerald-500/10',
    glassIconColor: 'text-white/70',
    glassIconBg: 'bg-white/[0.06]',
    default_details: {
      conditions: [
        {
          variable_display_name: 'Temperature',
          operator: '<',
          operator_phrase: 'below',
          threshold: 0,
          unit: '°C',
        },
      ],
    },
    required_inputs: ['Temperature threshold', 'Location scope'],
  },

  // Market Signal — Power (threshold)
  {
    id: 'market-power',
    name: 'Power Generation Alert',
    description:
      'Monitor wind, solar, or load forecasts across European market zones.',
    category: 'Market signal',
    notification_type: 'threshold',
    icon: Zap,
    iconColor: 'text-amber-400',
    iconBg: 'bg-amber-500/10',
    glassIconColor: 'text-white/70',
    glassIconBg: 'bg-white/[0.06]',
    default_details: {
      conditions: [
        {
          variable_display_name: 'Wind Generation',
          operator: '>',
          operator_phrase: 'above',
          threshold: 15,
          unit: 'GW',
        },
      ],
    },
    required_inputs: ['Variable', 'Threshold value', 'Market zone'],
  },

  // Model Revision (correction) — single unified preset
  {
    id: 'revision-significant',
    name: 'Big Forecast Change',
    description:
      'Get alerted when a new forecast changes a lot from the previous run. Configure by percentage or absolute value.',
    category: 'Model revision',
    notification_type: 'correction',
    icon: ArrowLeftRight,
    iconColor: 'text-purple-400',
    iconBg: 'bg-purple-500/10',
    glassIconColor: 'text-white/70',
    glassIconBg: 'bg-white/[0.06]',
    default_details: {
      conditions: [
        {
          variable_display_name: 'Wind Generation',
          operator: 'change',
          operator_phrase: 'change',
          threshold: 10,
          unit: '%',
        },
      ],
    },
    required_inputs: ['Change threshold', 'Variable', 'Unit (% or GW)'],
  },

  // Model Divergence
  {
    id: 'model-divergence',
    name: 'Models Disagree',
    description:
      'Get alerted when forecast models start to differ significantly on the same variable.',
    category: 'Model revision',
    notification_type: 'model_divergence',
    icon: GitCompareArrows,
    iconColor: 'text-purple-400',
    iconBg: 'bg-purple-500/10',
    glassIconColor: 'text-white/70',
    glassIconBg: 'bg-white/[0.06]',
    default_details: {
      models: [],
      conditions: [
        {
          variable_display_name: 'Wind Speed',
          operator: '>',
          operator_phrase: 'spread exceeds',
          threshold: 15,
          unit: '%',
        },
      ],
    },
    required_inputs: ['Models to compare', 'Variable', 'Divergence threshold'],
  },
]

// --- Context ---

interface AlertRulesContextType {
  rules: AlertRule[]
  addRule: (rule: AlertRule) => void
  updateRule: (id: string, updates: Partial<AlertRule>) => void
  deleteRule: (id: string) => void
  getPreset: (id: string) => AlertPreset | undefined
}

const AlertRulesContext = createContext<AlertRulesContextType | undefined>(
  undefined,
)

export function AlertRulesProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [rules, setRules] = useState<AlertRule[]>([])

  useEffect(() => {
    const stored = localStorage.getItem('jua-alert-rules')
    if (stored) {
      try {
        setRules(JSON.parse(stored))
      } catch (e) {
        console.error('Failed to parse alert rules', e)
      }
    }
  }, [])

  useEffect(() => {
    localStorage.setItem('jua-alert-rules', JSON.stringify(rules))
  }, [rules])

  const addRule = (rule: AlertRule) => {
    setRules((prev) => [rule, ...prev])
  }

  const updateRule = (id: string, updates: Partial<AlertRule>) => {
    setRules((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, ...updates, updated_at: new Date().toISOString() }
          : r,
      ),
    )
  }

  const deleteRule = (id: string) => {
    setRules((prev) => prev.filter((r) => r.id !== id))
  }

  const getPreset = (id: string) => PRESETS.find((p) => p.id === id)

  const value = {
    rules,
    addRule,
    updateRule,
    deleteRule,
    getPreset,
  }

  return React.createElement(AlertRulesContext.Provider, { value }, children)
}

export function useAlertRules() {
  const context = useContext(AlertRulesContext)
  if (context === undefined) {
    throw new Error('useAlertRules must be used within a AlertRulesProvider')
  }
  return context
}
